import React, { useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Grid, TextField } from '@mui/material';
import { Refresh, Search } from "@mui/icons-material";
import Button from "@mui/material/Button";
import { toast } from 'react-toastify';
import { InvalidMessage } from "../../Utility/Constants";
import Table from '../../Table/Table';
import { FormularyColumns } from '../../Table/TableColumn';
import { tableColumns } from '../../Redux/TableSlice';
import { currentData, clearData, updateSearch, searchClicked, updateInputError } from '../../Redux/MNR/FormularySlice';
import { clearAll, searchTriggered } from '../../Redux/CNS/CommonSlice';
import {
    validateRegexWithNumbers, validateRegexNumbersSpecialCharLong
} from '../../Validation/Validation';
import { GeneralError } from '../../Utility/Constants';
import Spinner from '../../Spinner/Spinner';
import { showAll, isMNR } from '../../Redux/MNR/MNRCommonSlice';

function Formulary() {
    const dispatch = useDispatch();
    const error = useSelector(state => state.formulary.error);
    const isSearched = useSelector(state => state.formulary.isSearchClicked);
    const searchTriggeredValue = useSelector(state => state.cnsCommon.searchTriggered);
    const inputError = useSelector(state => state.formulary.inputError);
    const hasAnyError = Object.values(inputError).some(value => value === true);
    const data = useSelector(state => state.formulary.data);
    const isSearchEnable = ((data.FormID && data.FormID.trim() !== '') ||
        (data.GPI && data.GPI.trim() !== '') ||
        (data.PlanCode5 && data.PlanCode5.trim() !== ''));
    const loading = useSelector(state => state.formulary.loading);
    const showAllValue = useSelector(state => state.mnrCommon.showAll);

    useEffect(() => {
        if (error) {
            toast.error(GeneralError);
            return;
        }
    }, [error]);

    const handleResetInitial = (useCallback(() => {
        dispatch(clearAll());
        dispatch(clearData());
        dispatch(updateSearch(true));
        dispatch(searchClicked(false));
        dispatch(showAll(false));
        dispatch(isMNR(true));
    }, [dispatch]))

    const handleReset = () => {
        dispatch(clearData());
        dispatch(updateSearch(true));
        dispatch(searchClicked(false));
        dispatch(showAll(false));
    }

    useEffect(() => {
        handleResetInitial();
        dispatch(tableColumns(FormularyColumns));
    }, [dispatch, handleResetInitial])

    const handleChange = (name, value) => {
        const val = value.target.value || value.target.innerText;
        switch (name) {
            case 'GPI':
                const updateRegexWithoutNumbersError = validateRegexNumbersSpecialCharLong(name, val, inputError);
                dispatch(updateInputError(updateRegexWithoutNumbersError));
                break;
            case 'FormID':
                const updateRegexWithNumbers = validateRegexWithNumbers(name, val, inputError);
                dispatch(updateInputError(updateRegexWithNumbers));
                break;
            case 'PlanCode5':
                const updateRegexWithNumbersToo = validateRegexWithNumbers(name, val, inputError);
                dispatch(updateInputError(updateRegexWithNumbersToo));
                break;
            default:
                break;
        }

        const updatedData = {
            ...data,
            [name]: val
        };
        dispatch(currentData(updatedData));
    }

    const handleSearch = () => {
        dispatch(showAll(false));
        dispatch(searchClicked(true));
        dispatch(searchTriggered(!searchTriggeredValue));
    }

    const handleShowAll = () => {
        dispatch(showAll(true));
        dispatch(clearData());
        dispatch(searchClicked(false));
        dispatch(searchTriggered(!searchTriggeredValue));
    }

    return (
        <>
            {loading && <Spinner />}
            <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6, md: 1.714285714285714, lg: 1.714285714285714, xl: 1.714285714285714 }}>
                    <TextField
                        id="planCode5"
                        label="PlanCode5"
                        variant="outlined"
                        size="small"
                        onChange={(e) => handleChange('PlanCode5', e)}
                        value={data.PlanCode5}
                        error={inputError.PlanCode5}
                        helperText={inputError.PlanCode5 ? InvalidMessage : ''}
                        fullWidth
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 1.714285714285714, lg: 1.714285714285714, xl: 1.714285714285714 }}>
                    <TextField
                        id="formID"
                        label="FormID"
                        variant="outlined"
                        size="small"
                        onChange={(e) => handleChange('FormID', e)}
                        value={data.FormID}
                        error={inputError.FormID}
                        helperText={inputError.FormID ? InvalidMessage : ''}
                        fullWidth
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 1.714285714285714, lg: 1.714285714285714, xl: 1.714285714285714 }}>
                    <TextField
                        id="GPI"
                        label="GPI"
                        variant="outlined"
                        size="small"
                        onChange={(e) => handleChange('GPI', e)}
                        value={data.GPI}
                        error={inputError.GPI}
                        helperText={inputError.GPI ? InvalidMessage : ''}
                        fullWidth
                    />
                </Grid>
            </Grid>
            <Grid container spacing={3} sx={{ justifyContent: 'center', paddingTop: '24px' }}>
                <Button
                    variant="contained"
                    onClick={handleShowAll}
                >
                    Show All
                </Button>
                <Button
                    variant={(!isSearched && !showAllValue) ? "contained" : "outlined"}
                    style={isSearched ? { color: 'var(--primary-font-color)' } : undefined}
                    size="small"
                    onClick={handleReset}
                    endIcon={<Refresh />}
                    disabled={!isSearched && !showAllValue}
                >
                    Reset
                </Button>
                <Button
                    variant={(!isSearchEnable || hasAnyError) ? "contained" : "contained"}
                    size="large"
                    onClick={handleSearch}
                    endIcon={<Search />}
                    style={(isSearchEnable && !hasAnyError) ? { color: 'var(--primary-font-color)', background: 'var(--primary-color)', border: '1px solid var(--primary-font-color)' } : undefined}
                    disabled={!isSearchEnable || hasAnyError}
                >
                    Search
                </Button>
            </Grid>
            {((isSearched) || (showAllValue)) && <Table />}
        </>
    )
}

export default Formulary;